import * as React from 'react';
import {
    calendarAgendaToggle,
    collapseAgendaPaneTitle,
    expandAgendaPaneTitle,
} from 'owa-calendar-shared-strings/lib/strings/calendarAgenda.locstring.json';
import {
    CalendarAgendaView,
    isAgendaViewShown,
    toggleAgendaView,
} from 'owa-publishedcalendar-agendaview';
import CalendarEvent from 'owa-calendar-types/lib/types/CalendarEvent';
import canShowAgendaPane from '../selectors/canShowAgendaPane';
import { ControlIcons } from 'owa-control-icons';
import { IconButton } from '@fluentui/react/lib/Button';
import loc from 'owa-localize';
import { observer } from 'mobx-react-lite';
import { OwaDate } from 'owa-datetime';
import { getFirstFocusable } from '@fluentui/react/lib/Utilities';

const styles = require('./CalendarAgendaPane.scss');

/**
 * Calendar agenda pane properties
 */
export interface CalendarAgendaPaneProps {
    date: OwaDate;
    events: CalendarEvent[];
}

export interface CalendarAgendaPaneHandle {
    setFocus(): void;
}

/**
 * Calendar agenda pane UI
 */
export default observer(
    function CalendarAgendaPane(
        props: CalendarAgendaPaneProps,
        ref: React.Ref<CalendarAgendaPaneHandle>
    ) {
        const { date, events } = props;
        const agendaContainer = React.useRef<HTMLDivElement>();
        const isAgendaPaneShown = isAgendaViewShown();
        /**
         * Publically exposed function that allows parent to set focus the agenda
         */
        React.useImperativeHandle(
            ref,
            () => ({
                setFocus() {
                    if (isAgendaPaneShown) {
                        if (!agendaContainer.current || !agendaContainer.current.focus) {
                            throw new Error(
                                'Cannot set focus on agenda when component ref is not initialized'
                            );
                        }
                        let node = agendaContainer.current;
                        let firstFocusable = getFirstFocusable(
                            node /* rootElement */,
                            node /* currentElement */,
                            true /* includeElementsInFocusZones */
                        );
                        if (firstFocusable) {
                            firstFocusable.focus();
                        }
                    }
                },
            }),
            []
        );

        if (!canShowAgendaPane()) {
            return null;
        }

        return (
            <div className={isAgendaPaneShown ? styles.agendaPane : undefined}>
                {isAgendaPaneShown && (
                    <div className={styles.agendaContent} ref={agendaContainer}>
                        <CalendarAgendaView date={date} events={events} />
                    </div>
                )}
                <div className={isAgendaPaneShown ? styles.footerContainer : undefined}>
                    <IconButton
                        title={
                            isAgendaPaneShown
                                ? loc(collapseAgendaPaneTitle)
                                : loc(expandAgendaPaneTitle)
                        }
                        className={
                            !isAgendaPaneShown ? styles.openAgendaPaneIconRoot : styles.iconRoot
                        }
                        styles={{ icon: styles.icon }}
                        iconProps={{
                            iconName: isAgendaPaneShown
                                ? ControlIcons.ClosePane
                                : ControlIcons.OpenPane,
                        }}
                        onClick={toggleAgendaView}
                        role="button"
                        aria-expanded={isAgendaPaneShown}
                        aria-label={loc(calendarAgendaToggle)}
                    />
                </div>
            </div>
        );
    },
    { forwardRef: true }
);
